import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, TrendingUp, BarChart3, FileText, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { OfferLeadForm } from '@/components/forms/OfferLeadForm';
import { supabase } from '@/integrations/supabase/client';
import { gaEvents, useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  theme: string;
  favicon_url: string | null;
  tenant_id: string | null;
  canonical_base_url: string | null;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  realtor_assigned?: boolean;
}

interface TownData {
  id: string;
  name: string;
  slug: string;
}

interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url: string | null;
  title_preference?: string | null;
}

export default function MarketValueReportPage() {
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteData | null>(null);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [areas, setAreas] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [loading, setLoading] = useState(true);

  useGoogleAnalytics(site?.tenant_id || null);

  useEffect(() => {
    fetchSiteData();
  }, []);

  // Track offer page view
  useEffect(() => {
    if (site && !loading) {
      gaEvents.viewOffer('market_value_report', '/market-value-report');
    }
  }, [site, loading]);

  const fetchSiteData = async () => {
    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const isDevelopment = hostname === 'localhost' || hostname.includes('lovable.app');
      
      let siteData: SiteData | null = null;

      if (!isDevelopment) {
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww}`)
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (!siteData && isDevelopment) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: siteAdmin } = await supabase
            .from('site_admins')
            .select('site_id')
            .eq('user_id', user.id)
            .single();

          if (siteAdmin) {
            const { data } = await supabase
              .from('sites_public' as any)
              .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
              .eq('id', siteAdmin.site_id)
              .maybeSingle();
            siteData = data as unknown as SiteData;
          }
        }
      }

      if (!siteData) {
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (siteData) {
        // Redirect if feature is disabled
        if (siteData.enable_market_value_report === false) {
          navigate('/', { replace: true });
          return;
        }
        
        setSite(siteData);

        // Fetch towns
        const { data: townsData } = await supabase
          .from('towns')
          .select('id, name, slug')
          .eq('site_id', siteData.id)
          .eq('is_published', true)
          .order('name');
        setTowns(townsData || []);
        
        // If only one town, fetch its areas for the AreaNavBar
        if (townsData && townsData.length === 1) {
          const { data: areasData } = await supabase
            .from('areas')
            .select('id, name, slug')
            .eq('town_id', townsData[0].id)
            .eq('is_published', true)
            .order('name');
          
          if (areasData) setAreas(areasData);
        }

        // Fetch realtor
        const { data: realtorData } = await supabase
          .from('realtor_profiles')
          .select('first_name, last_name, photo_url, phone, city, state, license_numbers, about_me, logo_url, title_preference, ad_image_url')
          .eq('site_id', siteData.id)
          .single();
        setRealtor(realtorData);
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Site not found</p>
      </div>
    );
  }

  const showRealtorContent = site.realtor_assigned !== false;
  const realtorName = showRealtorContent && realtor?.first_name && realtor?.last_name
    ? `${realtor.first_name} ${realtor.last_name}`
    : null;

  const canonicalUrl = site.canonical_base_url 
    ? `${site.canonical_base_url}/market-value-report`
    : null;

  const navTowns = towns.map(t => ({ id: t.id, name: t.name, slug: t.slug }));

  const benefits = [
    { icon: TrendingUp, title: 'Current Market Value', description: 'Get an accurate estimate of what your home is worth in today\'s market based on recent sales data.' },
    { icon: BarChart3, title: 'Comparable Sales Analysis', description: 'See what similar homes in your neighborhood have sold for in the past 6 months.' },
    { icon: Home, title: 'Neighborhood Trends', description: 'Understand how property values in your area are changing over time.' },
    { icon: FileText, title: 'Personalized Report', description: 'Receive a detailed PDF report you can use for refinancing, estate planning, or listing preparation.' },
  ];

  return (
    <PublicThemeProvider themeId={site.theme} headerFontId={site.header_font}>
      <div className="min-h-screen bg-background flex flex-col">
        <Helmet>
          <title>Free Market Value Report | {site.name}</title>
          <meta name="description" content={`Get a free, no-obligation home value report for your property in ${site.location_served}. Find out what your home is worth today.`} />
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
          
          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={site.name} />
          <meta property="og:title" content={`Free Market Value Report | ${site.name}`} />
          <meta property="og:description" content={`Get a free, no-obligation home value report for your property in ${site.location_served}. Find out what your home is worth today.`} />
          {/* og:url and og:image are injected by Cloudflare worker */}
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`Free Market Value Report | ${site.name}`} />
          <meta name="twitter:description" content={`Get a free, no-obligation home value report for your property in ${site.location_served}.`} />
          
          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
        </Helmet>

        <Header 
          siteName={site.name} 
          faviconUrl={site.favicon_url}
          logoUrl={realtor?.logo_url}
          showAdmin={false}
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableCommunityGuide={site.enable_community_guide !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />

        <TownNavBar 
          towns={navTowns}
          currentTownSlug=""
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
          realtorFirstName={realtor?.first_name}
          themeId={site.theme}
        />
        
        {/* Area Navigation Bar - Show when only one town exists */}
        {towns.length === 1 && areas.length > 0 && (
          <AreaNavBar areas={areas} townSlug={towns[0].slug} />
        )}

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-16 md:py-24" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)' }}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
              <h1 className="text-3xl md:text-5xl font-display font-bold mb-4 text-white">
                What's Your Home Worth Today?
              </h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90">
                Get a free Local Market Value Report with real neighborhood data - no obligation, no pressure.
              </p>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-12 md:py-16">
            <div className="container px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
                {/* Left: Content - 70% */}
                <div className="lg:col-span-7 space-y-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
                      Your Free Market Value Report Includes:
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Whether you're thinking about selling, refinancing, or just curious about your home's value, 
                      our comprehensive report gives you the information you need to make informed decisions.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    {benefits.map((benefit, index) => (
                      <Card key={index} className="bg-muted/50">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-full bg-accent/20 shrink-0">
                              <benefit.icon className="h-6 w-6 text-accent-foreground" />
                            </div>
                            <div>
                              <h3 className="font-semibold mb-1">{benefit.title}</h3>
                              <p className="text-sm text-muted-foreground">{benefit.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="bg-secondary/30 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-accent-foreground" />
                      No Strings Attached
                    </h3>
                    <p className="text-muted-foreground">
                      Your report is completely free with no obligation. {realtorName || 'Your local agent'} provides 
                      this service to help homeowners in {site.location_served} understand their home's value - 
                      whether you're ready to sell now or just planning for the future.
                    </p>
                  </div>

                  {/* Form moved to left column */}
                  <Card className="p-6 lg:p-8">
                    <h3 className="text-2xl font-display font-bold mb-2 text-center">
                      Request Your Free Report
                    </h3>
                    <p className="text-muted-foreground text-center mb-6">
                      Fill out the form below and {realtorName || 'we'}'ll prepare your personalized market value report.
                    </p>
                    <OfferLeadForm 
                      siteId={site.id}
                      requestType="market_value_report"
                      pagePath="/market-value-report"
                      submitButtonText="Get My Free Report"
                      siteDomain={window.location.hostname}
                    />
                  </Card>
                </div>

                {/* Right: Sidebar - 30% */}
                <aside className="lg:col-span-3 space-y-6">
                  {/* Realtor Card */}
                  {showRealtorContent && realtor && (
                    <RealtorCard realtor={realtor} />
                  )}
                </aside>
              </div>
            </div>
          </section>
        </main>

        <Footer 
          siteName={site.name} 
          locationServed={site.location_served}
          towns={navTowns}
          areas={areas}
          realtor={showRealtorContent ? realtor : null}
          enableBuyers={true}
          enableSellers={true}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />
      </div>
    </PublicThemeProvider>
  );
}
